import { FC, FormEvent, forwardRef, MouseEvent, Ref } from 'react';
import cn from 'classnames';
import * as RadixCheckbox from '@radix-ui/react-checkbox';

import { Icon } from '../../atoms/icon/Icon';
import { Text } from '../../atoms/text/Text';

import './Checkbox.scss';

interface WithClassNames {
  classNames?: cn.Argument;
}

export interface CheckboxRootProps extends RadixCheckbox.CheckboxProps, WithClassNames {}
export const CheckboxRoot = forwardRef<HTMLButtonElement, CheckboxRootProps>(
  ({ classNames = [], ...props }: CheckboxRootProps, ref) => (
    <RadixCheckbox.Root {...props} ref={ref} className={cn('lsux-cb-container__input', classNames)} />
  ),
);
CheckboxRoot.displayName = 'CheckboxRoot';

export interface CheckboxIndicatorProps extends RadixCheckbox.CheckboxIndicatorProps, WithClassNames {}
export const CheckboxIndicator = forwardRef<HTMLSpanElement, CheckboxIndicatorProps>(
  ({ classNames = [], ...props }: CheckboxIndicatorProps, ref) => (
    <RadixCheckbox.CheckboxIndicator {...props} ref={ref} className={cn('lsux-cb-container__icon', classNames)} />
  ),
);
CheckboxIndicator.displayName = 'CheckboxIndicator';

export type CheckboxVariant = 'filled' | 'outlined';

export interface CheckboxProps extends CheckboxRootProps {
  /**
   * Determines if input is checked or not.
   * @default false
   */
  checked?: boolean;
  /**
   * Determines if the input can be of mixed state ('minus' icon when checked).
   * @default false
   */
  mixed?: boolean;
  /**
   * Determines if the checkbox is disabled or not.
   * @default false
   */
  disabled?: boolean;
  /**
   * Left label text for the checkbox.
   */
  leftLabel?: string;
  /**
   * Right label text for the checkbox.
   */
  rightLabel?: string;
  /**
   * Style of the checkbox. Either filled or outlined.
   * @default filled
   */
  variant?: CheckboxVariant;
  /**
   * ID of the input.
   */
  id?: string;
  /**
   * Name of the input.
   */
  name: string;
  /**
   * Callback function that is called when the checkbox is clicked.
   */
  onChange?: (e: FormEvent<HTMLButtonElement>) => void | unknown;
  /**
   * Additional data that is dispatched with the tracking event.
   */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void | unknown;
  /**
   * Callback function that is called when the checkbox is clicked.
   */
  onCheckedChange?: (c: boolean | null) => void; // TODO: Add docs for this
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLButtonElement>;
}

const CheckboxComponent = forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      checked = false,
      classNames = [],
      disabled = false,
      leftLabel,
      mixed = false,
      id = 'c1',
      name,
      rightLabel,
      onChange,
      onClick,
      variant = 'filled',
      ...props
    }: CheckboxProps,
    ref,
  ) => {
    const classCheckboxContainer = 'lsux-cb-container';
    const classCheckboxElement = `${classCheckboxContainer}__cb`;
    const cnCheckbox = cn({
      [`${classCheckboxElement}`]: true,
      [`${classCheckboxElement}--disabled`]: disabled,
      [`${classCheckboxElement}--checked`]: checked,
      [`${classCheckboxElement}--mixed`]: mixed,
      [`${classCheckboxElement}--${variant}`]: variant,
      ['ml-3']: leftLabel,
      ['mr-3']: rightLabel,
    });

    const classIndictator = `${classCheckboxElement}--indicator`;

    const handleCheckedValue = (state: boolean | 'indeterminate') => {
      const newState = !!state || state === 'indeterminate' ? true : false;
      if (props.onCheckedChange && !disabled) {
        props.onCheckedChange(newState);
      }
    };

    return (
      <form data-testid={classCheckboxContainer} className={cn('lsux-cb-container', classNames)}>
        {leftLabel && (
          <label htmlFor={id} className="lsux-cb-container__label">
            <Text text={leftLabel} />
          </label>
        )}
        <RadixCheckbox.Root
          {...props}
          id={id}
          className={cnCheckbox}
          name={name}
          ref={ref}
          checked={checked}
          data-state={checked ? 'checked' : 'unchecked'}
          onCheckedChange={handleCheckedValue}
          onChange={onChange}
          onClick={onClick}
        >
          <RadixCheckbox.Indicator className={classIndictator}>
            {checked === true && !mixed ? <Icon name="interface_check" /> : <Icon name="edit_remove_minus" />}
          </RadixCheckbox.Indicator>
        </RadixCheckbox.Root>
        {rightLabel && (
          <label htmlFor={id} className="lsux-cb-container__label">
            <Text text={rightLabel} />
          </label>
        )}
      </form>
    );
  },
) as FC<CheckboxProps>;

CheckboxComponent.displayName = 'Checkbox';

type CheckboxComp = FC<CheckboxProps> & {
  Root: FC<CheckboxRootProps>;
  Indicator: FC<CheckboxIndicatorProps>;
};

export const Checkbox = CheckboxComponent as CheckboxComp;

Checkbox.Root = CheckboxRoot;
Checkbox.Indicator = CheckboxIndicator;
